/**
 * kisiKullaniciService.js — Kişi Detay › Kullanıcılar sekmesi: kişiye bağlı FDS kullanıcıları.
 * Backlog #380 (backend #377).
 *
 * Bir kişiye BİRDEN ÇOK kullanıcı bağlanabilir, bir kullanıcı en çok BİR kişiye. Bağlantı yalnızca "bu kullanıcı kim"
 * kaydıdır: kullanıcıya hiçbir yetki vermez, kullanıcıyı kişinin kendi girişi yapmaz. Kullanıcılar ESKİ SİSTEMDE
 * (PHP) oluşturulur; burada yalnızca bu veritabanında zaten olan kullanıcılar bağlanır (yenisi ilk girişinde gelir).
 * Okumalar YALNIZCA SAKA personeli (firma 403); yazmalar ek olarak `kisi.kullanici.manage` ister (Admin/SuperAdmin) —
 * okuma yanıtındaki `duzenleyebilir` bunu söyler.
 *
 * Satır şekli: `{ baglantiId, userId, kullaniciVar, kullaniciAdi, ad, soyad, adSoyad, unvan, telefon, eposta, aktif,
 *   sonGiris, uyrukId, userGroupId, grupAdi, personel, firmaId, firmaAdi, firmaYabanci, firmaSayisi, baglayanUserId,
 *   baglayanAdi, baglamaTarihi }` (+ `kaldiranUserId, kaldiranAdi, kaldirmaTarihi` `gecmis`te). Tarihler "yyyy-MM-ddTHH:mm:ss".
 * ⚠️ Okumalar zarfsız düz nesne, yazmalar `{message, result, data}` zarfıdır.
 *
 * Aday araması için `GET /users/kullanici-listesi` KULLANILMAZ: o `page.kullanici` (yalnızca Admin) ister ve kişiye
 * bağlı olup olmadığını / kişiyle eşleşmeyi bilmez. `kullanici-adaylari` tam bu seçici için yazıldı.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner. 409/400'de zarfın `data`sı anlamlıdır
// (UYARI_ONAYI_GEREKLI → `uyarilar[]`; KULLANICI_BASKA_KISIYE_BAGLI → `bagliPersonId`, `bagliAdSoyad`):
// personnelAdminService.createPersonAdmin'in BENZER_KAYIT işleyişinin genellemesi — hata `.status`, `.kod`, `.data` taşır.
function parseError(error) {
  const govde = error.response?.data;
  const message = !error.response
    ? 'Sunucu yanıt vermedi.'
    : typeof govde === 'string' && govde
      ? govde
      : govde?.message ?? error.message ?? 'İşlem başarısız oldu.';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = govde?.data ?? null;
  wrapped.kod = wrapped.data?.kod ?? null;
  throw wrapped;
}

/** Yazma yanıtının zarfını açar; `result:false` HTTP 200 ile de gelebilir. */
function zarfiAc(response) {
  const govde = response.data;
  if (govde && typeof govde === 'object' && govde.result === false) {
    const hata = new Error(govde.message || 'İşlem başarısız oldu.');
    hata.status = response.status;
    throw hata;
  }
  return govde?.data ?? null;
}

/**
 * Kişiye bağlı kullanıcılar: `{ personId, birlestirildigiPersonId, duzenleyebilir, kullanicilar[], gecmis[] }` —
 * şu an bağlı olanlar / bağlantısı kaldırılmış olanlar (yeniden eskiye). Birleştirilmiş (gizli) kart üzerinde
 * kartta kalanı gösterir. 404: kişi yok.
 * GET /api/v1/personnel/{personId}/kullanicilar
 */
export async function getKisiKullanicilari(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/kullanicilar`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kullanıcıyı kişiye bağlar. Dönüş = okuma şekli + `{ degisti, uyarilar[] }` (zaten bağlıysa `degisti: false`).
 * Hatalar (`.kod` ile dallan, mesaja değil):
 *   · 409 UYARI_ONAYI_GEREKLI — ad soyad farklı/boş; `.data.uyarilar` = `[{ kod, mesaj }]`; `uyarilarOnaylandi: true` ile tekrar
 *   · 409 KULLANICI_BASKA_KISIYE_BAGLI — `.data.bagliPersonId`, `.data.bagliAdSoyad`; önce orada kaldırılmalı
 *   · 409 BIRLESTIRILMIS_KART · 404 kişi/kullanıcı yok · 403 yetki yok
 * POST /api/v1/personnel/{personId}/kullanicilar — `{ userId, uyarilarOnaylandi? }`
 */
export async function baglaKisiKullanici(personId, userId, uyarilarOnaylandi = false) {
  let response;
  try {
    response = await axios.post(
      `${BASE}/${personId}/kullanicilar`,
      { userId, uyarilarOnaylandi: Boolean(uyarilarOnaylandi) },
      { headers: getAuthHeader() },
    );
  } catch (error) {
    parseError(error);
  }
  return zarfiAc(response);
}

/**
 * Bağlantıyı kaldırır; satır `gecmis`te kalır (kullanıcı silinmez). Dönüş = okuma şekli + `degisti`.
 * 404: kullanıcı şu an bu kişiye bağlı değil · 409: birleştirilmiş kart.
 * DELETE /api/v1/personnel/{personId}/kullanicilar/{userId}
 */
export async function kaldirKisiKullanici(personId, userId) {
  let response;
  try {
    response = await axios.delete(`${BASE}/${personId}/kullanicilar/${userId}`, { headers: getAuthHeader() });
  } catch (error) {
    parseError(error);
  }
  return zarfiAc(response);
}

/**
 * "Kullanıcı Bağla" seçicisinin adayları: `{ personId, q, oneri, adaylar[], fazlaVar }`, en iyi eşleşme önde, en çok 50.
 *   · `q` boş → ÖNERİLER (`oneri: true`): ad soyadı, telefonu (son 10 hane) ya da e-postası kişiyle eşleşen kullanıcılar
 *   · `q` ≥ 2 karakter → arama: kullanıcı adı, ad, soyad, e-posta, telefon (4+ hane), kullanıcı no (1 karakter 400 döner)
 * Her aday okuma satırı + `eslesme[AD_SOYAD|TELEFON|EPOSTA]`, `bagliPersonId`, `bagliAdSoyad`, `buKisiyeBagli`,
 * `baskaKisiyeBagli`. Yalnızca SAKA personeli (yazma yetkisi gerekmez; bağlama anında denetlenir).
 * GET /api/v1/personnel/{personId}/kullanici-adaylari?q=
 */
export async function araKullanici(personId, q = '') {
  try {
    const metin = String(q ?? '').trim();
    const response = await axios.get(`${BASE}/${personId}/kullanici-adaylari`, {
      params: metin ? { q: metin } : undefined,
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}
